/**
 * Click-to-reveal for text that spoils the thing it is describing.
 *
 * `whatYouMissIfSkipped` is the obvious case: to explain honestly what you give
 * up by skipping an entry, it has to name the payoff — who dies, who returns,
 * what the post-credits scene sets up. That is exactly the text someone
 * planning a first watch must not read by accident.
 *
 * Blurred rather than collapsed, so the layout doesn't jump when it opens and
 * you can see how much there is before committing. Revealing is one-way: this
 * is not a thing anyone wants to re-hide, and a toggle would imply otherwise.
 */

import { useState } from "react";
import styles from "./Spoiler.module.css";

export function Spoiler({ children, label = "Reveal" }: { children: string; label?: string }) {
  const [revealed, setRevealed] = useState(false);

  if (revealed) return <span className={styles.revealed}>{children}</span>;

  return (
    <button type="button" className={styles.spoiler} onClick={() => setRevealed(true)}>
      {/* The spoiler itself stays hidden from assistive tech until revealed —
       * announcing it while describing the control that hides it would defeat
       * the point. */}
      <span aria-hidden="true" className={styles.masked}>
        {children}
      </span>
      {/* This is the accessible name, and it is also the visible text. An
       * aria-label used to supply the name instead, which failed WCAG 2.5.3
       * (label in name): a speech-input user saying "spoilers, click to read"
       * — the words actually on screen — matched nothing. */}
      <span className={styles.hint}>{`Spoilers, click to read ${label.toLowerCase()}`}</span>
    </button>
  );
}
